import { CourseContainers } from '@yandex-int/k-common';
import {
    getContainerModules,
    GetContainerModulesQueryParams,
} from '@yandex-int/k-common/client/api/defs/get-container-modules';
import { getCourseContainers } from '@yandex-int/k-common/client/api/defs/get-course-containers';
import { getModuleLessons } from '@yandex-int/k-common/client/api/defs/get-module-lessons';
import { useAppContext, useViewDeps } from 'platform-components';
import { QueryConfig, useQuery } from 'react-query';
import { getUniqueQueryKeys } from 'utils/getUniqueQueryKey';

import { getLessonsCounts } from 'store/courses/selectors';

export const queryKeys = getUniqueQueryKeys(['getModuleLessons', 'getContainerModules', 'getCourseContainers']);

/**
 * Загрузка всех курс-контейнеров. В качестве ключа для обновления используется id курса и распределение занятий,
 * так как курс-контейнеры и их баннеры ссылаются на последнее выданное занятие.
 * @param {number | null} courseId id курса для получения всех курс-контейнеров
 * @param lessonsCounts {ReturnType<typeof getLessonsCounts>} распределение занятий в курсе по выданным,
 * завершенным и черновикам
 * @param config {QueryConfig} конфиг запроса (см. подробнее QueryConfig в useQuery)
 */
export function useCourseContainersQuery(
    courseId?: number | null,
    lessonsCounts?: ReturnType<typeof getLessonsCounts>,
    config?: QueryConfig<any>
) {
    return useQuery<CourseContainers>(
        [queryKeys.getCourseContainers, courseId, lessonsCounts],
        () => getCourseContainers(courseId!),
        {
            enabled: Boolean(courseId),

            // 3 вместо 10 секунд, тк пользователь успевает добавить курс
            // и вернуться на страницу, а курсы не обновляются EDUCATION-31299
            staleTime: 2000,
            ...config,
        }
    );
}

/**
 * Загрузка модулей курс-контейнера.
 * @param {string} containerSlug слаг курс-контейнера
 * @param queryParams query параметры
 * @param {number | null} queryParams.courseId id курса
 * дополнительно добавятся группы уроков в каждый модуль с количеством выдач и завершений в курсе
 * @param {boolean} queryParams.withLessons
 * дополнительно добавятся группы уроков в каждый модуль с нулевым количеством выдач и завершений
 */
export function useModulesQuery(containerSlug: string, queryParams: GetContainerModulesQueryParams = {}) {
    const initialData = useViewDeps(getContainerModules);
    const { initialRender } = useAppContext();
    const { courseId, withLessons } = queryParams;

    return useQuery(
        [queryKeys.getContainerModules, containerSlug, courseId, withLessons],
        () => getContainerModules(containerSlug, queryParams),
        {
            initialData: () => (initialRender ? initialData : undefined),
            initialStale: true,
            enabled: Boolean(containerSlug),
            staleTime: 10000,
        }
    );
}

export function useModuleLessonsQuery(moduleId: number, courseId?: number) {
    const params = { moduleId, courseId };

    return useQuery([queryKeys.getModuleLessons, moduleId, courseId], () => getModuleLessons(params), {
        enabled: !!moduleId,
        staleTime: 10000,
    });
}
